import React, { useState } from "react";

import {
  Padding,
  ReportTitle,
  Sheet,
  Text,
  Layout,
  AnchorLink,
  Margin,
  Button,
} from "@kidslivesafe/sc-react";
import {
  useAppSelector,
  useHandlePageNavigate,
  useAppDispatch,
} from "../../../../hooks/hooks";
import { selectBrandInfo } from "../../../../features/brand/brandSlice";
import {
  selectPrimarySubscription,
  selectDownsellDetails,
  selectSubscriptionStatus,
  downsellSubscriptionAsync,
  getSubscriptionDetailsAsync,
  selectIsInTrial,
  selectIsInRebill,
} from "../../../../features/subscriptions/subscriptionSlice";
import "./cancel-options.css";
import { useCurrentBreakpoint } from "@kidslivesafe/sc-foundation";
import { useCancelSubscription } from "../../../../hooks/cancelHook";
import { sendMemberEventAsync } from "../../../../features/admin/adminSlice";
import { usePauseSubscription } from "../../../../hooks/pauseHook";
import PauseConfirmationModal from "../../../../components/UIComponents/PauseConfirmationModal/PauseConfirmationModal";
import { selectMemberData } from "../../../../features/authentication/authenticationSlice";

const CancelOptions: React.FC = () => {
  const [showPausedModal, setShowPausedModal] = useState<boolean>(false);
  const pauseSubscription = usePauseSubscription();
  const pageNavigate = useHandlePageNavigate();
  const dispatch = useAppDispatch();
  const memberData = useAppSelector(selectMemberData);

  const brandInfo = useAppSelector(selectBrandInfo);
  const subscriptionDetails = useAppSelector(selectPrimarySubscription);
  const downsellData = useAppSelector(selectDownsellDetails);
  const currentBreakpoint = useCurrentBreakpoint();
  const cancelSubscription = useCancelSubscription();
  const loading = useAppSelector(selectSubscriptionStatus) === "loading";
  const isInTrial = useAppSelector(selectIsInTrial);
  const isInRebill = useAppSelector(selectIsInRebill);

  const calculateDownsellSavings = () => {
    if (!downsellData || !subscriptionDetails) return 0;

    const originalPrice = subscriptionDetails.price;
    const downsellPrice = downsellData.downSellPrice;

    if (originalPrice <= 0 || downsellPrice <= 0) return 0;

    const savings = ((originalPrice - downsellPrice) / originalPrice) * 100;
    return Math.floor(savings);
  };

  return (
    <>
      <ReportTitle reportTitle="How to Cancel" />
      <Sheet>
        <Padding
          top={currentBreakpoint === "desktop" ? "xl" : "md"}
          left="md"
          right="md"
          bottom="md"
        >
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "md",
            }}
          >
            <Layout type="flex" flexProps={{ gap: "sm", direction: "column" }}>
              <Text tag="h2">Can We Help Before You Leave?</Text>
              <Text>
                We’re sorry to see you go! Before you finish, you might find one
                of these options a better fit for your needs:{" "}
                {downsellData?.isDownsold || !downsellData
                  ? ""
                  : "a discounted rate, "}
                a temporary pause, or a full cancellation. You’re in control.
              </Text>
            </Layout>
            {downsellData?.isDownsold || !downsellData ? null : (
              <>
                <Layout
                  type="flex"
                  flexProps={{ gap: "sm", direction: "column" }}
                >
                  <Margin top="xs">
                    <Text tag="h2">
                      Don’t Lose Your Benefits—Save Over{" "}
                      {calculateDownsellSavings()}%!
                    </Text>
                  </Margin>

                  <Layout
                    type="flex"
                    flexProps={{ gap: "md", direction: "column" }}
                  >
                    <Text>
                      We'd hate to see you miss out! For just{" "}
                      <Text tag="b">
                        ${downsellData?.downSellPrice} every{" "}
                        {subscriptionDetails?.billingInterval} days
                      </Text>
                      , you can keep the key benefits of your {brandInfo.name}{" "}
                      membership at a fraction of the price.
                    </Text>
                  </Layout>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{ gap: "sm", direction: "column" }}
                >
                  <Text tag="h3">You'll still enjoy</Text>
                  <ul className="page-cancel-options__list">
                    <li>
                      <Text>
                        All Your Past Reports: Your search and report history
                        stays accessible
                      </Text>
                    </li>
                    <li>
                      <Text>
                        Up to {downsellData.downSellPeopleReportThreshold} New
                        Reports Monthly: People, phone, or property lookups as
                        you need them
                      </Text>
                    </li>
                    {brandInfo.acronym === "pdc" ? (
                      <li>
                        <Text>
                          Up to {downsellData.downSellVehicleReportThreshold}{" "}
                          New Vehicle Reports Monthly
                        </Text>
                      </li>
                    ) : null}
                    <li>
                      <Text>
                        Continuous Monitoring: Get notified of important updates
                      </Text>
                    </li>
                    <li>
                      <Text>
                        Email Alerts: Stay informed whenever something changes
                      </Text>
                    </li>
                  </ul>
                  <Text>
                    Enjoy peace of mind (for less). You can cancel anytime.
                  </Text>
                  <Layout
                    type="flex"
                    flexProps={{ gap: "sm", direction: "column" }}
                    breakpointStyles={{
                      desktop: {
                        type: "flex",
                        flexProps: {
                          gap: "sm",
                          direction: "column",
                          alignItems: "flex-start",
                        },
                      },
                    }}
                  >
                    <Button
                      buttonText={`Keep My Access for $${downsellData?.downSellPrice}`}
                      color="secondary"
                      handleClick={() => {
                        if (loading) return;
                        dispatch(downsellSubscriptionAsync()).then(action => {
                          if (
                            downsellSubscriptionAsync.fulfilled.match(action)
                          ) {
                            dispatch(getSubscriptionDetailsAsync());
                            pageNavigate("/offer-confirmation");
                          }
                        });
                        dispatch(
                          sendMemberEventAsync({
                            EventCategory: "Cancel Options",
                            EventAction: "Down Sell Clicked",
                            MetaData: JSON.stringify({
                              key: "CARL - Cancel Funnel",
                              value: "Down Sell",
                            }),
                          }),
                        );
                      }}
                      size="md"
                    />
                    <AnchorLink
                      role="button"
                      handleClick={() => {
                        pageNavigate("/");
                      }}
                      inline
                      hoverUnderline
                      size="sm"
                    >
                      No thanks, not now
                    </AnchorLink>
                  </Layout>
                </Layout>
              </>
            )}
            {(memberData && memberData.subscriptionStatus === "PAUSED") ||
            isInTrial ||
            isInRebill ? null : (
              <Layout
                type="flex"
                flexProps={{ gap: "sm", direction: "column" }}
              >
                <Margin top="xs">
                  <Text tag="h2">Need a Break? Pause Your Account.</Text>
                </Margin>
                <Text>
                  Not sure if you’re ready to cancel? You can pause your
                  membership for 30 days with no charges, no loss of data, and
                  you can reactivate anytime you’re ready.
                </Text>
                <Text>When you pause:</Text>
                <ul className="page-cancel-options__list">
                  <li>
                    <Text>No payments while paused</Text>
                  </li>
                  <li>
                    <Text>All your reports and history are saved</Text>
                  </li>
                  <li>
                    <Text>Instant access when you return</Text>
                  </li>
                </ul>
                <Text>
                  It’s the stress-free way to take a break without losing your
                  membership benefits.
                </Text>
                <Layout
                  type="flex"
                  flexProps={{ gap: "sm", direction: "column" }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        gap: "sm",
                        direction: "column",
                        alignItems: "flex-start",
                      },
                    },
                  }}
                >
                  <Button
                    buttonText="Pause"
                    color="secondary"
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Cancel Options",
                          EventAction: "Pause Membership",
                          MetaData: JSON.stringify({
                            key: "CARL - Cancel Funnel",
                            value: "Pause Membership",
                          }),
                        }),
                      );
                      pauseSubscription();
                      setShowPausedModal(true);
                    }}
                    size="md"
                  />
                  <AnchorLink
                    role="button"
                    handleClick={() => {
                      pageNavigate("/");
                    }}
                    inline
                    hoverUnderline
                    size="sm"
                  >
                    No thanks, not now
                  </AnchorLink>
                </Layout>
              </Layout>
            )}

            <Layout type="flex" flexProps={{ gap: "sm", direction: "column" }}>
              <Margin top="xs">
                <Text tag="h3">We're sorry to see you go.</Text>
              </Margin>
              <Layout
                type="flex"
                flexProps={{ gap: "md", direction: "column" }}
              >
                <Text>Your cancellation will take effect immediately.</Text>
                <ul className="page-cancel-options__list">
                  <li>
                    <Text>You will no longer be billed</Text>
                  </li>
                  <li>
                    <Text>
                      Your access to reports and monitoring will continue until
                      the end of the current billing
                    </Text>
                  </li>
                  <li>
                    <Text>
                      You can always reactivate your account at any time. Log in
                      when you’re ready
                    </Text>
                  </li>
                </ul>
                <Text>Thank you for being a {brandInfo.name} member.</Text>
                <Layout
                  type="flex"
                  flexProps={{ gap: "sm", direction: "column" }}
                  breakpointStyles={{
                    desktop: {
                      type: "flex",
                      flexProps: {
                        gap: "sm",
                        direction: "column",
                        alignItems: "flex-start",
                      },
                    },
                  }}
                >
                  <Button
                    buttonText="Cancel"
                    color="secondary"
                    handleClick={() => {
                      dispatch(
                        sendMemberEventAsync({
                          EventCategory: "Cancel Options",
                          EventAction: "Cancel Membership",
                          MetaData: JSON.stringify({
                            key: "CARL - Cancel Funnel",
                            value: "Cancel Membership",
                          }),
                        }),
                      );
                      cancelSubscription();
                    }}
                    size="md"
                  />
                  <AnchorLink
                    role="button"
                    handleClick={() => {
                      pageNavigate("/");
                    }}
                    inline
                    hoverUnderline
                    size="sm"
                  >
                    No thanks, keep me active
                  </AnchorLink>
                </Layout>
              </Layout>
            </Layout>
          </Layout>
        </Padding>
      </Sheet>
      <PauseConfirmationModal
        showPausedModal={showPausedModal}
        setShowPausedModal={setShowPausedModal}
      />
    </>
  );
};

export default CancelOptions;
